import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import api from '../api';

import ClientesList from './ClientesList';
import AssinaturasAdmin from './AssinaturasAdmin';
import AdicionaLicencas from './AdicionaLicencas';
import ListarLicencas from './ListarLicencas';
import AdicionaPagamentos from './AdicionaPagamentos';
import ListarPagamentos from './ListarPagamentos';
import PlanosAdmin from './PlanosAdmin';
import ResetSenha from './ResetSenha';
import Suporte from './Suporte';
import { useAuth } from '../auth';
import './estilodash.css';

function fmtMoney(v) {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function DashboardHome({ clientes, assinaturas, licencas, pagamentos, authLogs }) {
  const ativos = clientes.filter(c => String(c.status) === '1');
  const assinaturasAtivas = assinaturas.filter(a => String(a.status) === '1');
  const licAtivas = licencas.filter(l => String(l.status) === '1');
  const licEA = licAtivas.filter(l => l.codigo_plano === 'ea' || l.produto_codigo === 'ea').length;
  const licBias = licAtivas.filter(l => l.codigo_plano === 'bias' || l.produto_codigo === 'bias').length;
  const licRagnarok = licAtivas.filter(l => l.codigo_plano === 'ragnarok' || l.produto_codigo === 'ragnarok').length;
  const slotsTotais = assinaturasAtivas.reduce((s, a) => s + Number(a.slots_contratados || 0), 0);
  const receitaMes = pagamentos
    .filter(p => {
      const st = String(p.status || '').toLowerCase();
      return st !== 'cancelado';
    })
    .reduce((s, p) => s + Number(p.valor_calculado ?? p.valor ?? 0), 0);

  const ultimosLogs = authLogs.slice(0, 10);

  return (
    <div className="dashboard-home">
      <h2>Visão Geral do Novo Modelo</h2>
      <p className="dashboard-home-subtitle">Clientes, assinaturas, licenças por robô e financeiro por assinatura.</p>

      <div className="kpi-container">
        <div className="kpi-card"><h3>Clientes</h3><p>{clientes.length}</p><small>Ativos: {ativos.length}</small></div>
        <div className="kpi-card"><h3>Assinaturas</h3><p>{assinaturas.length}</p><small>Ativas: {assinaturasAtivas.length}</small></div>
        <div className="kpi-card"><h3>Licenças ativas</h3><p>{licAtivas.length}</p><small>EA {licEA} | Bias {licBias} | Ragnarok {licRagnarok}</small></div>
        <div className="kpi-card"><h3>Slots contratados</h3><p>{slotsTotais}</p><small>Soma das assinaturas ativas</small></div>
        <div className="kpi-card"><h3>Receita estimada</h3><p>R$ {fmtMoney(receitaMes)}</p><small>Baseada nos pagamentos / valor_calculado</small></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 24 }}>
        <div>
          <h3>Licenças por robô</h3>
          <table className="clientes-table">
            <thead><tr><th>Robô</th><th>Licenças ativas</th></tr></thead>
            <tbody>
              <tr><td>Quantum Trades EA</td><td>{licEA}</td></tr>
              <tr><td>QuantumTrades Bias</td><td>{licBias}</td></tr>
              <tr><td>QuantumTrades Ragnarok</td><td>{licRagnarok}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <h3>Últimos logs do robô</h3>
          <table className="clientes-table">
            <thead><tr><th>Conta</th><th>Robô</th><th>Resultado</th><th>Motivo</th></tr></thead>
            <tbody>
              {ultimosLogs.length === 0 ? <tr><td colSpan="4">Nenhum log carregado.</td></tr> : ultimosLogs.map((l) => (
                <tr key={l.id}>
                  <td>{l.conta || '—'}</td>
                  <td>{l.produto_nome || l.robo_codigo || '—'}</td>
                  <td>{Number(l.sucesso) === 1 ? 'OK' : 'Falha'}</td>
                  <td>{l.motivo || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ logout }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [assinaturas, setAssinaturas] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [pagamentos, setPagamentos] = useState([]);
  const [authLogs, setAuthLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const carregarResumo = async () => {
    try {
      setLoading(true);
      const [c, a, l, p, logs] = await Promise.all([
        api.get('/api/clientes'),
        api.get('/api/assinaturas'),
        api.get('/api/licencas-v2'),
        api.get('/api/pagamentos'),
        api.get('/api/auth-logs-recentes?limit=20'),
      ]);
      setClientes(c.data || []);
      setAssinaturas(a.data || []);
      setLicencas(l.data || []);
      setPagamentos(p.data || []);
      setAuthLogs(logs.data || []);
    } catch (e) {
      console.error('Erro ao carregar dashboard:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarResumo();
  }, []);

  const menu = useMemo(() => ([
    { label: 'Visão Geral', path: '' },
    { label: 'Clientes', path: 'clientes' },
    { label: 'Assinaturas', path: 'assinaturas' },
    { label: 'Adicionar Licença', path: 'licencas/adicionar' },
    { label: 'Listar Licenças', path: 'licencas' },
    { label: 'Adicionar Pagamento', path: 'pagamentos/adicionar' },
    { label: 'Listar Pagamentos', path: 'pagamentos' },
    { label: 'Catálogo', path: 'catalogo' },
    { label: 'Resetar Senha', path: 'reset-senha' },
    { label: 'Suporte', path: 'suporte' },
  ]), []);

  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="sidebar-title">Admin Quantum Trades</div>
        <div style={{ fontSize: 12, marginBottom: 14 }}>Logado como {user?.email || 'admin'}</div>
        <ul className="sidebar-list">
          {menu.map((item) => (
            <li className="sidebar-item" key={item.path}>
              <button type="button" onClick={() => navigate(`/dashboard/${item.path}`)}>{item.label}</button>
            </li>
          ))}
          <li className="sidebar-item">
            <button type="button" className="logout-button" onClick={logout}>Sair</button>
          </li>
        </ul>
      </aside>

      <main className="main-content">
        {loading ? <p>Carregando dashboard...</p> : (
          <Routes>
            <Route index element={<DashboardHome clientes={clientes} assinaturas={assinaturas} licencas={licencas} pagamentos={pagamentos} authLogs={authLogs} />} />
            <Route path="clientes" element={<ClientesList />} />
            <Route path="assinaturas" element={<AssinaturasAdmin />} />
            <Route path="licencas/adicionar" element={<AdicionaLicencas />} />
            <Route path="licencas" element={<ListarLicencas />} />
            <Route path="pagamentos/adicionar" element={<AdicionaPagamentos />} />
            <Route path="pagamentos" element={<ListarPagamentos />} />
            <Route path="catalogo" element={<PlanosAdmin />} />
            <Route path="reset-senha" element={<ResetSenha />} />
            <Route path="suporte" element={<Suporte />} />
          </Routes>
        )}
      </main>
    </div>
  );
}
